import { supabase } from '../supabase';
import {
  useQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { HistoricalUsage } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';

interface DailyModelListSlot {
  id: string;
  position: number;
  time: string;
  slot_period: 'all' | 'am' | 'pm';
  type: 'interaction' | 'promo' | 'text';
  model_id?: string;
  group_id?: string;
  old_model_id?: string;
}

export interface DailyModelListWithSlots {
  list_id: string;
  model_id: string;
  date: DateTime;
  slots: DailyModelListSlot[];
  created_at: DateTime;
  updated_at: DateTime;
}

class SupabaseModelDailyFormatService {

  async getDailyModelListForModel(
    modelId: string,
    date: DateTime
  ): Promise<DailyModelListSlot[]> {
    const { data, error } = await supabase
      .from('v_daily_model_lists_with_slots')
      .select('data')
      .eq('model_id', modelId)
      .eq('date', date.toISO()?.split('T')[0])
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get daily model list: ${error.message}`);
    }

    if (!data?.data?.slots) {
      return [];
    }

    return data.data.slots.map((slot: any) => ({
      id: slot.id,
      position: slot.position,
      time: slot.time,
      slot_period: slot.slot_period,
      type: slot.type,
      model_id: slot.model_id,
      old_model_id: slot.old_model_id,
      group_id: slot.group_id,
      metadata: slot.metadata
    }));
  }

  async getDailyModelListWithSlots(
    modelId: string,
    date: DateTime
  ): Promise<DailyModelListWithSlots | null> {
    const { data, error } = await supabase
      .from('v_daily_model_lists_with_slots')
      .select('data')
      .eq('model_id', modelId)
      .eq('date', date.toISO()?.split('T')[0])
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get daily model list: ${error.message}`);
    }

    if (!data?.data) {
      return null;
    }

    const listData = data.data;
    return {
      list_id: listData.list_id,
      model_id: listData.model_id,
      date: DateTime.fromISO(listData.date, { zone: 'UTC' }),
      created_at: DateTime.fromISO(listData.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(listData.updated_at, { zone: 'UTC' }),
      slots: (listData.slots || []).map((slot: any) => ({
        id: slot.id,
        position: slot.position,
        time: slot.time,
        slot_period: slot.slot_period,
        type: slot.type,
        model_id: slot.model_id,
        old_model_id: slot.old_model_id,
        group_id: slot.group_id
      }))
    };
  }

  async getHistoricalUsage(): Promise<HistoricalUsage | null> {
    const { data, error } = await supabase
      .rpc('get_historical_usage');

    if (error) {
      throw new Error(`Failed to get historical usage: ${error.message}`);
    }

    if (!data) return null;

    return {
      groups: data,
      updated_at: DateTime.utc()
    };
  }

  async regenerateModelLists(model_id: string, includeToday = false): Promise<{ generatedCount: number; model_id: string }> {
    const now = DateTime.now().setZone('Europe/London');

    const startDate = includeToday ? now.startOf('day') : now.plus({ days: 1 }).startOf('day');

    const daysToGenerate = includeToday ? 3 : 2;

    const messages: Array<{
      msg_id: string;
      modelId: string;
      date: string;
      isRegeneration: boolean;
    }> = [];

    for (let i = 0; i < daysToGenerate; i++) {
      const dayAtNoon = startDate.plus({ days: i }).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
      messages.push({
        msg_id: `regenerate-${model_id}-${dayAtNoon.toISODate()}`,
        modelId: model_id,
        date: dayAtNoon.toISO() || '',
        isRegeneration: true,
      });
    }

    console.log(messages);

    const { data, error } = await supabase.functions.invoke('model-list-generation-handler', {
      body: { messages },
    });

    if (error) {
      throw new Error(`Failed to regenerate model lists: ${error.message}`);
    }

    return {
      generatedCount: data?.processed ?? 0,
      model_id,
    };
  }

  async regenerateAllModelLists(start_date: DateTime, end_date: DateTime): Promise<{ generatedCount: number; }> {
    const { data, error } = await supabase.functions.invoke('model-list-regenerate-all', {
      body: {
        startDate: start_date,
        endDate: end_date
      }
    });

    if (error) {
      throw new Error(`Failed to regenerate all model lists: ${error.message}`);
    }

    return {
      generatedCount: data.data.generated_count
    }
  }

  async swapModelInSlot(slotId: string, newModelId: string, includeInHistory: boolean = true): Promise<any> {
    const { data, error } = await supabase.functions.invoke('model-list-swap-slot', {
      body: {
        slotId,
        newModelId,
        includeInHistory
      }
    });

    if (error) {
      throw new Error(`Failed to swap model: ${error.message}`);
    }

    return data.data;
  }

  async swapModelInLaunchSlot(slotId: string, newModelId: string, reverse: boolean = false): Promise<any> {
    const { data, error } = await supabase.functions.invoke('model-list-swap-launch-slot', {
      body: {
        slotId,
        newModelId,
        reverse
      }
    });

    if (error) {
      throw new Error(`Failed to swap model in launch slot: ${error.message}`);
    }

    return data.data;
  }

  async generateMissingModelLists(): Promise<{ generatedCount: number }> {
    const { data, error } = await supabase.functions.invoke('model-list-generate-missing');

    if (error) {
      throw new Error(`Failed to generate missing model lists: ${error.message}`);
    }

    return {
      generatedCount: data.data.generated_count
    };
  }

  async regenerateAllModelListsInRange(startDate: DateTime, endDate: DateTime): Promise<{ generatedCount: number; deletedCount: number }> {
    const { data, error } = await supabase.functions.invoke('model-list-regenerate-all', {
      body: {
        startDate: startDate.toISO(),
        endDate: endDate.toISO()
      }
    });

    if (error) {
      throw new Error(`Failed to regenerate all model lists: ${error.message}`);
    }

    return {
      generatedCount: data.data.generated_count,
      deletedCount: data.data.deleted_count
    };
  }

  async getModelFormatGenerationQueueSize(): Promise<number> {
    const { data, error } = await supabase
      .rpc('pgmq_metrics', { queue_name: 'model_format_generation_queue' });

    if (error) {
      throw new Error(`Failed to get queue size: ${error.message}`);
    }

    return data?.[0]?.queue_length || 0;
  }
}

const instance = new SupabaseModelDailyFormatService();

// React Query Hooks for Daily Model List Slots

export function useDailyModelListForModel(
  modelId: string | null,
  date: DateTime | null
) {
  return useQuery({
    queryKey: ['dailyModelList', modelId, date?.toISO()],
    queryFn: () => {
      if (!modelId || !date) return Promise.resolve([]);
      return instance.getDailyModelListForModel(modelId, date);
    },
    enabled: !!(modelId && date),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export const useHistoricalUsage = () => {
  return useQuery({
    queryKey: ['historicalUsage'],
    queryFn: () => instance.getHistoricalUsage(),
  });
};

export const useRegenerateModelLists = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { model_id: string; includeToday?: boolean }) =>
      instance.regenerateModelLists(params.model_id, params.includeToday),
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['dailyModelListSlots', data.model_id] });
      queryClient.invalidateQueries({ queryKey: ['dailyModelList'] });
      queryClient.invalidateQueries({ queryKey: ['historicalUsage'] });
    },
  });
};

export const useRegenerateAllModelLists = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { start_date: DateTime, end_date: DateTime }) =>
      instance.regenerateAllModelLists(params.start_date, params.end_date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyModelListSlots'] });
      queryClient.invalidateQueries({ queryKey: ['dailyModelList'] });
      queryClient.invalidateQueries({ queryKey: ['historicalUsage'] });
    },
  });
}

export const useSwapModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { slotId: string; newModelId: string; includeInHistory?: boolean }) =>
      instance.swapModelInSlot(params.slotId, params.newModelId, params.includeInHistory ?? true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyModelListSlots'] });
      queryClient.invalidateQueries({ queryKey: ['dailyModelList'] });
      queryClient.invalidateQueries({ queryKey: ['historicalUsage'] });
    },
  });
}

export const useSwapModelInLaunchSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { slotId: string; newModelId: string; reverse?: boolean }) =>
      instance.swapModelInLaunchSlot(params.slotId, params.newModelId, params.reverse ?? false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyModelListSlots'] });
      queryClient.invalidateQueries({ queryKey: ['dailyModelList'] });
      queryClient.invalidateQueries({ queryKey: ['historicalUsage'] });
    },
  });
}

export const useGenerateMissingModelLists = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => instance.generateMissingModelLists(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyModelListSlots'] });
      queryClient.invalidateQueries({ queryKey: ['dailyModelList'] });
      queryClient.invalidateQueries({ queryKey: ['historicalUsage'] });
    },
  });
}

export const useRegenerateAllModelListsInRange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { startDate: DateTime; endDate: DateTime }) =>
      instance.regenerateAllModelListsInRange(params.startDate, params.endDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyModelListSlots'] });
      queryClient.invalidateQueries({ queryKey: ['dailyModelList'] });
      queryClient.invalidateQueries({ queryKey: ['historicalUsage'] });
    },
  });
}

export const useModelFormatGenerationQueueSize = () => {
  return useQuery({
    queryKey: ['modelFormatGenerationQueueSize'],
    queryFn: () => instance.getModelFormatGenerationQueueSize(),
    refetchInterval: 2000,
    staleTime: 0,
  });
}

export default instance;
export type { DailyModelListSlot };